import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { isDevFlag, useConditionalFeature } from '@americanexpress/conditional-features';
import { useSelector } from 'react-redux';
import { getActiveLocaleSelector, getIsLoggedInSelector } from '@americanexpress/travel-selectors';
import { withRouter } from '@americanexpress/one-app-router';
import { Experiment, useFeatureFlag, Variant } from '@americanexpress/one-experiments';
import { Surface } from '@americanexpress/dls-react';
import { RenderModule } from 'holocron';
import { useOneCmsFeatureFlag } from '@americanexpress/consumer-travel-base';
import { useReactlytics } from '@americanexpress/reactlytics';
import {
  hasFooterLinksRule, hasForYouRule, hasLegalRule, hasProjectAdsRule, hasDisablePackagesRule,
  hasTravelCreditOverlayRule, hasVacationPackagesPathRule, hasVacationRentalsRule,
} from '../constants/featureRulesConstants';
import Hero from './Hero/Hero';
import PageMeta from './PageMeta/PageMeta';
import getLineOfBusinessFromPath from '../utils/getLineOfBusinessUtils';
import { generateNextPathFromLineOfBusiness } from '../utils/generateNextPathFromLineOfBusiness';
import Terms from './Terms/Terms';
import CardOffers from './CardOffersContainer/CardOffersContainer';
import { HOTELS } from '../constants/lineOfBusinessConstants';
import StrategicMessage from './StrategicMessage/StrategicMessage';
import { useMembershipData } from '../hooks/useMembershipDataHook';
import { isVREligible } from '../utils/membership';
import {
  DISABLE_PACKAGES_FF, VACATION_RENTALS_FF, MARKETING_TILES_EXPERIMENT, MOSAIC_SEARCH_WIDGET_FF,
} from '../constants/oneXpConstants';
import { InspirationArticles } from './InspirationalArticles/InspirationArticles';
import MosaicHero from './Hero/MosaicHero';
import {
  FOOTER_MODULE_NAME, LEGAL_MODULE_NAME, CREDIT_MODULE_NAME, ADS_MODULE_NAME, MODULE_NAME,
} from '../constants/modules';
import ForYou from './ForYou/ForYou';
import { MarketingTilesContainer } from './MarketingTiles/MarketingTilesContainer.tsx';
import '@americanexpress/consumer-travel-base/build/index.css';
import { oneCmsReporting } from '../fetchye/useOneCmsFetchye';
import { useFeatureFlagWithProperties } from '../thunks/useFeatureFlagWithProperties';
import { ExploreMore } from './ExploreMore/ExploreMore';

export const bindOnSelectedTabChange = ({
  selectedTab, setSelectedTab,
}) => (nextSelectedTab) => {
  if (selectedTab === null && nextSelectedTab === HOTELS) {
    return;
  }
  if (nextSelectedTab !== selectedTab) {
    setSelectedTab(nextSelectedTab);
  }
};

export const getInitialLineOfBusiness = (pathname, isPackagesDisabledByFF) => {
  if (pathname.endsWith('/travel') || pathname.endsWith('/travel/')) {
    return null;
  }
  return getLineOfBusinessFromPath(isPackagesDisabledByFF, pathname);
};

const HomePage = ({
  location: { pathname },
  router,
}) => {
  const { track } = useReactlytics();
  const locale = useSelector(getActiveLocaleSelector);
  const [, market] = locale.toLowerCase().split('-');
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const hasVacationPackagesPath = useConditionalFeature(hasVacationPackagesPathRule);

  const isPackagesDisabledByRule = useConditionalFeature(hasDisablePackagesRule);
  const isPackagesDisabledByFF = useFeatureFlagWithProperties(DISABLE_PACKAGES_FF);
  const isPackagesDisabled = isPackagesDisabledByRule || isPackagesDisabledByFF;
  const prevSelectedTab = useRef(getInitialLineOfBusiness(pathname, isPackagesDisabled));
  const [selectedTab, setSelectedTab] = useState(prevSelectedTab.current);

  const { data: membershipData } = useMembershipData();

  const isEligibleForVacationRentals = isVREligible(membershipData);
  const hasVacationRentals = useConditionalFeature(hasVacationRentalsRule);
  const { enabled: vrEnabled } = useFeatureFlag({ featureFlagName: VACATION_RENTALS_FF });

  const showVacationRentals = hasVacationRentals // Checks for eligible Locale
    && vrEnabled // Checks that the VR FF is enabled
    && (isEligibleForVacationRentals // Checks that the star eligible flag is set
      || !isLoggedIn); // Guest users should see the vr tab

  useEffect(() => {
    if (pathname.includes('demo')) {
      return;
    }
    if (selectedTab !== prevSelectedTab.current) {
      prevSelectedTab.current = selectedTab;
      router.push(
        generateNextPathFromLineOfBusiness(
          selectedTab, router.location, hasVacationPackagesPath
        )
      );
    }
  }, [selectedTab, locale, pathname, hasVacationPackagesPath, router]);

  const hasAds = useConditionalFeature(hasProjectAdsRule);
  const hasTravelCreditOverlay = useConditionalFeature(hasTravelCreditOverlayRule);
  const hasFooterLinks = useConditionalFeature(hasFooterLinksRule);
  const hasLegal = useConditionalFeature(hasLegalRule);

  const hasForYou = useConditionalFeature(hasForYouRule);
  const { isEnabled: hasMosaicHero } = useOneCmsFeatureFlag({
    flagName: MOSAIC_SEARCH_WIDGET_FF, fetchyeReporting: oneCmsReporting,
  });

  return (
    <>
      <PageMeta selectedTab={selectedTab} />
      {isDevFlag('strategicInitiative') && <StrategicMessage />}
      {hasMosaicHero ? (
        <MosaicHero
          selectedTab={selectedTab}
          setSelectedTab={bindOnSelectedTabChange({ selectedTab, setSelectedTab })}
          pathname={pathname}
        />
      ) : (
        <Hero
          setSelectedTab={bindOnSelectedTabChange({ selectedTab, setSelectedTab })}
          pathname={pathname}
        />
      )}
      {hasForYou && <ForYou />}
      <InspirationArticles />
      <Surface variant="foreground">
        <Experiment
          experimentName={MARKETING_TILES_EXPERIMENT}
          dimensionName="homepage"
          defaultVariant="control"
        >
          <Variant variant="control">
            <CardOffers />
          </Variant>
          <Variant variant="mosaic">
            <MarketingTilesContainer />
          </Variant>
        </Experiment>
      </Surface>
      <ExploreMore />
      {hasAds && (
        <RenderModule
          moduleName={ADS_MODULE_NAME}
          props={{
            page: 'travel', // remove when new version of ads module is deployed
            placementId: 'TravelWeb_Homepage_Mid_1', // + here
            backgroundColor: 'white', // + here
            placementType: 'FLUID', // + here
            channelName: 'TLS',
            placementName: 'HOMEPAGE_MID',
            onAdEvent: ({ type }) => {
              track({
                event: {
                  type,
                  element: `${market}hp-mid-ad`,
                  identifier: MODULE_NAME,
                },
              });
            },
          }}
        />
      )}
      {hasAds && (
      <RenderModule
        moduleName={ADS_MODULE_NAME}
        props={{
          page: 'travel', // remove when new version of ads module is deployed
          placementId: 'TravelWeb_Homepage_Bottom', // + here
          backgroundColor: 'white', // + here
          placementType: 'SMALL_BANNER', // + here
          channelName: 'TLS',
          placementName: 'HOMEPAGE_BOTTOM',
          onAdEvent: ({ type }) => {
            track({
              event: {
                type,
                element: `${market}hp-bottom-ad`,
                identifier: MODULE_NAME,
              },
            });
          },
        }}
      />
      )}
      {hasFooterLinks && <RenderModule moduleName={FOOTER_MODULE_NAME} />}
      <Terms showVacationRentals={showVacationRentals} />
      {hasLegal && <RenderModule moduleName={LEGAL_MODULE_NAME} />}
      {hasTravelCreditOverlay && (
        <RenderModule
          moduleName={CREDIT_MODULE_NAME}
          props={{ isOverlay: true }}
        />
      )}
    </>
  );
};

HomePage.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    query: PropTypes.shape({
      ERROR_MESSAGE: PropTypes.string,
    }),
  }).isRequired,
  router: PropTypes.shape({
    location: PropTypes.shape({}),
    push: PropTypes.func.isRequired,
  }).isRequired,
};

export { HomePage as TestableHomePage };

export default withRouter(HomePage);
